/**
 * Einstellungen → Firma → Karte „OpenTable“ (Antoine, 26.10.2026, Migration 740).
 *
 * Steuert den Knopf „In OpenTable eintragen“ auf der Vorgangsseite. Liefert die Werte über
 * versteckte Felder an das umgebende Formular (Muster `VorfreudeMailEinstellung`). Der Reservierungslink
 * für die Saison-Mails (`opentableUrl`) steht unverändert darüber im selben Formular.
 */
import { useState } from "react";
import { Input, Select, Toggle } from "./components";

const SPRACHEN = [
  { value: "de-DE", label: "Deutsch (de-DE)" },
  { value: "en-GB", label: "Englisch, UK (en-GB)" },
  { value: "en-US", label: "Englisch, USA (en-US)" },
  { value: "it-IT", label: "Italienisch (it-IT)" },
  { value: "fr-FR", label: "Französisch (fr-FR)" },
];

export interface OpenTableEinstellungWerte {
  opentableKnopfAktiv?: boolean | null;
  opentableRestaurantId?: string | null;
  opentableSprache?: string | null;
  opentableMaxGaeste?: number | null;
  opentableNurImHaus?: boolean | null;
}

export function OpenTableEinstellung({ werte, onAendern }: { werte: OpenTableEinstellungWerte; onAendern?: () => void }) {
  const [aktiv, setAktiv] = useState(Boolean(werte.opentableKnopfAktiv));
  const [nurImHaus, setNurImHaus] = useState(werte.opentableNurImHaus ?? true);
  const [sprache, setSprache] = useState(werte.opentableSprache || "de-DE");
  const optionen = SPRACHEN.some((s) => s.value === sprache) ? SPRACHEN : [...SPRACHEN, { value: sprache, label: sprache }];
  return (
    <div className="min-w-0 max-w-full space-y-4 rounded-xl border border-[#F0E7D6] px-4 py-4 dark:border-white/[0.06]" data-testid="opentable-einstellung">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">OpenTable: Knopf „In OpenTable eintragen“</p>
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Auf der Vorgangsseite öffnet der Knopf den Reservierungsdialog von OpenTable mit Datum, Uhrzeit und Gästezahl. MAESTRO schreibt nichts in OpenTable; Sie tragen die Gruppe dort selbst ein und markieren sie danach als eingetragen.
          </p>
        </div>
        <Toggle checked={aktiv} onChange={(v) => { setAktiv(v); onAendern?.(); }} label="Knopf In OpenTable eintragen aktiv" />
        <input type="hidden" name="opentableKnopfAktiv" value={aktiv ? "true" : "false"} />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Restaurant-ID</span>
          <Input name="opentableRestaurantId" inputMode="numeric" defaultValue={werte.opentableRestaurantId ?? ""} placeholder="z. B. 115809" />
        </label>
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Sprache</span>
          <Select ariaLabel="Sprache des OpenTable-Dialogs" value={sprache} onValueChange={(v) => { setSprache(v); onAendern?.(); }} options={optionen} />
          <input type="hidden" name="opentableSprache" value={sprache} />
        </label>
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Höchstzahl Gäste online</span>
          <Input name="opentableMaxGaeste" type="number" min={1} max={500} defaultValue={werte.opentableMaxGaeste ?? 50} />
        </label>
      </div>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Die Restaurant-ID steht im Link Ihres OpenTable-Reservierungsdialogs hinter „rid=“. Größere Gruppen bekommen keinen Link, sondern den Hinweis „zu groß für OpenTable online“.
      </p>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Nur bei Veranstaltungen „Im Haus“</p>
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Catering außer Haus belegt keinen Tisch und bekommt keinen Knopf. Ist der Ort noch nicht bestätigt, steht der Knopf ausgegraut da.
          </p>
        </div>
        <Toggle checked={nurImHaus} onChange={(v) => { setNurImHaus(v); onAendern?.(); }} label="Nur bei Veranstaltungen Im Haus" />
        <input type="hidden" name="opentableNurImHaus" value={nurImHaus ? "true" : "false"} />
      </div>
    </div>
  );
}
